import React, {useCallback, useEffect, useRef, useState} from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import {
  zoomImage,
  removeStorageKeyFromBrowser,
  savePointNameToStorage,
  saveActivePointToStorage,
  getChatIdFromBrowserStorage,
  sendUserEventToParent
} from '../../../../actions/static'
import { initialIframeSetting } from '../../../../app/config'
import CenteredLoader from '../../../view/CenteredLoader'
import {
  CLOSE,
  BIG_IMAGE,
  OPEN_WEBVIEW,
  QUESTIONNAIRE_CHAT_ID,
  OPPORTUNITY_TO_RETURN,
  QUESTIONNAIRE_CLOSE_IFRAME,
  QUESTIONNAIRE_IFRAME_STATE,
  QUESTIONNAIRE_DISABLE_BUTTON,
  QUESTIONNAIRE_CLOSE_IFRAME_SUCCESSFULLY,
  QUESTIONNAIRE_FULLSCREEN_IMAGE,
  QUESTIONNAIRE_OPEN_ACCORDION,
  QUESTIONNAIRE_CONTENT_LOADED
} from '../../../../constants/postMessageEvent'
import { detectBrowserType } from '../../../../helper/detectBrowserType'
import { browserType } from '../../../../constants/string'
import classes from './styles.module.scss'
import { LoaderWrapper } from './Iframe.style'

const Iframe = ({
  chatId,
  setIframe,
  isPanoramaPost,
  settings,
  iframeSettings,
  isMobile,
  isUserIdle,
  setAnchorMenu,
  setIsIframeContentLoaded,
  setIsImageZoomed,
  setIsLoadingIframeData,
  isLoadingIframeData,
  widgetIframeSource,
  setWidgetIframeSource,
  language
}) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const iframeRef = useRef(null)
  const { iframeSrc } = iframeSettings
  const urlParams = new URLSearchParams(iframeSrc)
  const currentQuestionnaireName = urlParams.get('metadata')
  const isQuestionnaire = iframeSrc.includes('questionnaire') || settings.availableQuestionnaires.includes(currentQuestionnaireName)
  const { t } = useTranslation()
  const i18nIframeTitleKey = isQuestionnaire ? 'questionnaire_iframe' : 'link_iframe'

  const closeIframe = useCallback(
    iframeSettings => {
      setIframe(iframeSettings)
    },
    [setIframe],
  )

  useEffect(() => {
    //create iframe loaded listener only in Safari
    if (detectBrowserType() === browserType.SAFARI) {
      iframeLoaded()
    }
    // add for close persistent menu inside click iframe
    const listener = () => setAnchorMenu(null)
    window.addEventListener('blur', listener)
    return () => window.removeEventListener('blur', listener)
  }, [])

  useEffect(() => {
    if (isUserIdle) {
      setIframe(initialIframeSetting)
    }
  }, [isUserIdle])

  useEffect(() => {
    if (!iframeSettings.isQuestionnaireIframeOpened && widgetIframeSource) {
      widgetIframeSource.postMessage({
        type: QUESTIONNAIRE_IFRAME_STATE,
        isQuestionnaireIframeOpened: false,
      }, '*')
    }
  }, [iframeSettings])

  const iframeLoaded = () => {
    const iframe = iframeRef.current
    iframe.focus()
    let doc = iframe.contentDocument || iframe.contentWindow

    if (doc.document) {
      doc = doc.document
    }

    const timer = setInterval(() => {
      if (doc.readyState === 'complete') {
        clearInterval(timer)
        setIsLoaded(true)
        setIsIframeContentLoaded(true)
        !isQuestionnaire && setIsLoadingIframeData(true)
      }
    }, 500)
  }

  const messageEventListener = useCallback(message => {
    processMessageByType(message)
    processMessageByName(message)
  }, [])

  function processMessageByType(message) {
    const {
      source,
      data: { type, isBackButtonPresent, isQuestionnaireIframeOpened, imageUrl, altText, eventName },
    } = message
    const copyIframeSettings = Object.assign({}, iframeSettings)

    switch (type) {
    case QUESTIONNAIRE_CHAT_ID:
      setWidgetIframeSource(message.source)
      saveActivePointToStorage(settings.chatHistoryLevel, 'activeQuestionnaireName', iframeSettings)
      initializeQuestionnaire(source)
      break
    case QUESTIONNAIRE_CLOSE_IFRAME:
      removeStorageKeyFromBrowser('activeQuestionnaireName')
      closeIframe(initialIframeSetting)
      break
    case OPPORTUNITY_TO_RETURN:
      copyIframeSettings.opportunityToManualClose = isBackButtonPresent
      copyIframeSettings.isLoaded = true
      setIframe(copyIframeSettings)
      break
    case QUESTIONNAIRE_IFRAME_STATE:
      copyIframeSettings.isQuestionnaireIframeOpened = isQuestionnaireIframeOpened
      copyIframeSettings.isLoaded = true
      setIframe(copyIframeSettings)
      break
    case QUESTIONNAIRE_CLOSE_IFRAME_SUCCESSFULLY:
      savePointNameToStorage(settings.chatHistoryLevel, iframeSettings, 'passedQuestionnairesName')
      removeStorageKeyFromBrowser('activeQuestionnaireName')
      closeIframe(initialIframeSetting)
      setIsIframeContentLoaded(false)
      setIsLoadingIframeData(false)
      break
    case QUESTIONNAIRE_DISABLE_BUTTON:
      removeStorageKeyFromBrowser('activeQuestionnaireName')
      setIframe(initialIframeSetting)
      break
    case QUESTIONNAIRE_FULLSCREEN_IMAGE:
      zoomImage(imageUrl, altText)
      setIsImageZoomed(true)
      break
    case QUESTIONNAIRE_OPEN_ACCORDION:
      sendUserEventToParent(eventName)
      break
    case QUESTIONNAIRE_CONTENT_LOADED:
      setIsLoadingIframeData(true)
      break
    }
  }

  function processMessageByName(message) {
    const {
      data: { event, location },
    } = message

    switch (event) {
    case OPEN_WEBVIEW:
      openWebview(message)
      break
    case BIG_IMAGE:
      zoomImage(location)
      setIsImageZoomed(true)
      break
    case CLOSE:
      const copyIframeSettings = Object.assign({}, initialIframeSetting)
      copyIframeSettings.questionnaireName = iframeSettings.questionnaireName
      closeIframe(copyIframeSettings)
      break
    }
  }

  function initializeQuestionnaire(source) {
    const chatIdFromStorage = getChatIdFromBrowserStorage()
    source.postMessage({
      type: QUESTIONNAIRE_CHAT_ID,
      chatId: chatIdFromStorage ? chatIdFromStorage : chatId,
      language
    },
    '*',
    )
  }

  function openWebview(message) {
    const webview = message.data.webview
    if (webview?.extension === 'true') {
      setIframe({
        iframeSrc: webview.url,
        isIframeOpen: true,
        questionnaireName: iframeSettings.questionnaireName,
      })
    } else if (webview?.extension === 'false') {
      window.open(webview.url, `_newTab${Math.ceil(Math.random() * 9999)}`)
    }
  }

  useEffect(() => {
    setIframe({
      ...iframeSettings,
      isLoaded: true,
    })
    window.addEventListener('message', messageEventListener)
    return () => {
      window.removeEventListener('message', messageEventListener)
      setWidgetIframeSource(null)
    }
  }, [])

  const showIframe = () => {
    if (!isLoaded) {
      setIsLoaded(true)
      setIsIframeContentLoaded(true)
      !isQuestionnaire && setIsLoadingIframeData(true)
      iframeRef.current.focus()
    }
  }

  const setMetadataForQuestionnaireSrc = () => {
    const { iframeSrc } = iframeSettings
    if (iframeSrc.includes('questionnaire')) {
      return iframeSrc.endsWith('questionnaire&metadata=')
        ? `${iframeSrc}${iframeSettings.questionnaireName}`
        : iframeSrc
    }

    return iframeSrc
  }

  return (
    <div className={classes.iframeWrapper}>
      <LoaderWrapper isPanoramaPost={isPanoramaPost} isMobile={isMobile} isLoadingIframeData={isLoadingIframeData}>
        <CenteredLoader borderTopColor={settings.color} size={48} />
      </LoaderWrapper>
      <iframe
        style={{ opacity: isLoadingIframeData ? 1 : 0 }}
        ref={iframeRef}
        title={t(i18nIframeTitleKey)}
        className={classNames(classes.iframe, isLoaded && classes.iframeLoaded, isPanoramaPost && classes.panoramaIframe)}
        src={setMetadataForQuestionnaireSrc()}
        onError={() => closeIframe(initialIframeSetting)}
        onLoad={showIframe}
        name={`${iframeSettings.iframeSrc}_iframe`}
      >
        <p>Your browser does not support iframes.</p>
      </iframe>
    </div>
  )
}

Iframe.propTypes = {
  color: PropTypes.string,
  setIframe: PropTypes.func.isRequired,
  device: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  iframeSettings: PropTypes.shape({
    isLoaded: PropTypes.bool.isRequired,
    iframeSrc: PropTypes.string.isRequired,
    isIframeOpen: PropTypes.bool.isRequired,
    questionnaireName: PropTypes.string.isRequired,
    opportunityToManualClose: PropTypes.bool.isRequired,
  }),
}

export default Iframe
